import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  TextField,
  Typography
} from '@mui/material';
import useSWR from 'swr';
import PartnerPortalApi from '../../api/partnerPortal';
import type { PortalCompliance as PortalComplianceData } from '../../api/partnerPortal';
import PortalLayout from './PortalLayout';
import PortalProfile from './PortalProfile';
import PortalDocuments from './PortalDocuments';
import PortalCompliance from './PortalCompliance';
import PortalProjects from './PortalProjects';
import Api from '../../api';
import blueBerryLogoMini from '../../assets/images/bluberry_logo_mini.png';
import blueBerryLetters from '../../assets/images/bluberry.png';

function PartnerPortal() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [activeTab, setActiveTab] = useState('profile');
  const [showLogin, setShowLogin] = useState(false);
  const [email, setEmail] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    // If token in URL, store it and validate
    if (token) {
      localStorage.setItem('partnerToken', token);
    }

    const storedToken = localStorage.getItem('partnerToken');
    if (!storedToken) {
      setShowLogin(true);
      setLoading(false);
      return;
    }

    PartnerPortalApi.getProfile()
      .then((profile) => {
        setCompanyName(profile.companyName);
        setLoading(false);
      })
      .catch(() => {
        localStorage.removeItem('partnerToken');
        setShowLogin(true);
        setLoading(false);
      });
  }, [token]);

  const { data: complianceData } = useSWR(
    !showLogin && !loading ? 'portal-compliance-nav' : null,
    PartnerPortalApi.getCompliance
  );

  const { data: portalProfile } = useSWR(
    !showLogin && !loading ? 'portal-profile-nav' : null,
    PartnerPortalApi.getProfile
  );

  const getComplianceNavStatus = (
    data?: PortalComplianceData | null
  ): 'compliant' | 'pending' | 'rejected' | 'missing' | null => {
    if (!data) return null;
    if (data.compliant) return 'compliant';
    if (data.rejected.length > 0) return 'rejected';
    if (data.pending.length > 0) return 'pending';
    if (data.missing.length > 0) return 'missing';
    return null;
  };

  const handleRequestAccess = async () => {
    if (!email.trim()) return;
    setSending(true);
    try {
      await Api.PartnerApi.requestPortalAccess(email);
      setEmailSent(true);
    } catch (e) {
      setError('Something went wrong. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('partnerToken');
    navigate('/partner-portal');
    setShowLogin(true);
    setCompanyName('');
  };

  if (loading) {
    return (
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          justifyContent: 'center',
          minHeight: '100vh'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (showLogin) {
    return (
      <Container
        sx={{
          alignItems: 'center',
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          padding: '48px 16px'
        }}
      >
        <Box
          sx={{
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
            maxWidth: 440,
            padding: '40px',
            width: '100%'
          }}
        >
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              gap: 1,
              justifyContent: 'center',
              mb: 3
            }}
          >
            <img
              alt="Bluberry"
              src={blueBerryLogoMini}
              style={{ height: 32 }}
            />
            <img alt="Bluberry" src={blueBerryLetters} style={{ height: 20 }} />
          </Box>
          <Typography
            sx={{
              fontWeight: 500,
              mb: 1,
              textAlign: 'center'
            }}
            variant="h5"
          >
            Partner portal
          </Typography>

          {emailSent ? (
            <>
              <Typography
                color="success.main"
                sx={{ mb: 2, mt: 2, textAlign: 'center' }}
                variant="body2"
              >
                Access link sent! Check your email and click the link to open
                your portal.
              </Typography>
              <Button
                fullWidth
                onClick={() => {
                  setEmailSent(false);
                  setEmail('');
                }}
                sx={{ textTransform: 'none' }}
                variant="outlined"
              >
                Send another link
              </Button>
            </>
          ) : (
            <>
              <Typography
                color="text.secondary"
                sx={{ mb: 3, textAlign: 'center' }}
                variant="body2"
              >
                Enter your email to receive a link to access your partner
                portal.
              </Typography>
              {error && (
                <Typography
                  color="error"
                  sx={{ mb: 2, textAlign: 'center' }}
                  variant="body2"
                >
                  {error}
                </Typography>
              )}
              <TextField
                fullWidth
                label="Email"
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleRequestAccess();
                }}
                size="small"
                sx={{ mb: 2 }}
                type="email"
                value={email}
              />
              <Button
                fullWidth
                disabled={sending}
                onClick={handleRequestAccess}
                sx={{ textTransform: 'none' }}
                variant="contained"
              >
                {sending ? (
                  <CircularProgress color="inherit" size={20} />
                ) : (
                  'Send access link'
                )}
              </Button>
            </>
          )}
        </Box>
      </Container>
    );
  }

  const renderContent = () => {
    switch (activeTab) {
      case 'profile':
        return <PortalProfile />;
      case 'projects':
        return <PortalProjects />;
      case 'documents':
        return <PortalDocuments />;
      case 'compliance':
        return <PortalCompliance />;
      default:
        return <PortalProfile />;
    }
  };

  return (
    <PortalLayout
      activeTab={activeTab}
      companyName={companyName}
      complianceStatus={getComplianceNavStatus(complianceData)}
      onLogout={handleLogout}
      onTabChange={setActiveTab}
      partnerStatus={portalProfile?.status}
    >
      {renderContent()}
    </PortalLayout>
  );
}

export default PartnerPortal;
